import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { Header, TabNavigation } from '@/components/shared';
import { AthleteDetailData } from '@/types';
import { getApiUrl } from '@/config/api';
import { OverviewTab, RecoveryTab, SleepTab, ActivityTab, AIInsightsTab, ReadinessScore } from '@/components/athlete-detail';
import { generateCommands } from '@/config/commands';
import { useModals } from '@/contexts/ModalContext';
import { BarChart3, Bot } from '@/utils/icons';
import DashboardLayout from '@/components/shared/DashboardLayout';
import { isAnyWearableConnected, getConnectedProviderLabel, ENABLED_PROVIDER_IDS, getConnectedProviderIds } from '@/config/wearableProviders';
import { shiftDateForward } from '@/utils/dateUtils';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);



/**
 * AthleteDetail Component - Displays comprehensive athlete analysis with AI insights
 *
 * Features:
 * - Recovery, sleep, and activity data visualization
 * - AI-powered performance analysis using OpenAI GPT-4.1
 * - Interactive charts and trend analysis
 * - Coaching recommendations and insights
 */
const AthleteDetail: React.FC = () => {
  const navigate = useNavigate();
  const { teamId, athleteId } = useParams<{ teamId: string; athleteId: string }>();
  const { isDark } = useTheme();
  const { openCreateUserModal, openInviteUserModal, openCreateTeamModal, openCreateOrgModal } = useModals();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'overview';

  const [athleteData, setAthleteData] = useState<AthleteDetailData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState<boolean>(false);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [selectedDataSources, setSelectedDataSources] = useState<string[]>(ENABLED_PROVIDER_IDS);

  // Derive which providers are connected for this athlete
  const connectedSources = useMemo(() => {
    if (!athleteData?.athlete_info) return [];
    return getConnectedProviderIds(athleteData.athlete_info);
  }, [athleteData?.athlete_info]);

  // Keep selectedDataSources in sync with connected providers
  useEffect(() => {
    if (connectedSources.length === 0) {
      setSelectedDataSources([]);
      return;
    }
    const filtered = selectedDataSources.filter((s: string) => connectedSources.includes(s));
    const next = filtered.length > 0 ? filtered : connectedSources;
    if (next.length !== selectedDataSources.length || next.some((s: string, i: number) => s !== selectedDataSources[i])) {
      setSelectedDataSources(next);
    }
  }, [connectedSources]);

  /**
   * Data validation functions to exclude non-wearing periods and invalid data
   */








  /**
   * Calculate validated stats from clean data
   */
  const getValidatedStats = () => {
    if (!athleteData) return null;

    const now = new Date();
    const isWithinLastNDays = (dateStr: string, n: number) => {
      const d = new Date(dateStr);
      const start = new Date(now.getTime() - n * 24 * 60 * 60 * 1000);
      return d >= start && d <= now;
    };

    const weightedAverage = (values: number[]) => {
      if (values.length === 0) return 0;
      // Linear recency weighting: newer points weigh more
      const weights = values.map((_, i) => i + 1);
      const sumW = weights.reduce((a, b) => a + b, 0);
      const sumWV = values.reduce((acc, v, i) => acc + v * weights[i], 0);
      return sumWV / sumW;
    };

    // Recovery Stats (last 7 days, recency-weighted)
    const validRecoveryData = (athleteData.recovery.recovery_data || [])
      .filter(((data: any) => data && data.recovery_score))
      .filter(r => isWithinLastNDays(r.date, 7))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const recoveryScores = validRecoveryData.map(r => r.recovery_score);

    const recoveryStats = {
      average_recovery: weightedAverage(recoveryScores),
      latest_recovery: recoveryScores.length > 0 ? recoveryScores[recoveryScores.length - 1] : 0,
      data_points: validRecoveryData.length
    };

    // Sleep Stats (last 7 days, recency-weighted)
    const validSleepData = (athleteData.sleep.sleep_data || [])
      .filter((data: any) => data && data.total_duration_hours)
      .filter(s => isWithinLastNDays(s.date, 7))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const sleepQualityScores = validSleepData.map(s => s.sleep_quality_score).filter((v): v is number => v != null && v > 0);
    const sleepDurations = validSleepData.map(s => s.total_duration_hours);

    const sleepStats = {
      average_quality: weightedAverage(sleepQualityScores),
      latest_quality: sleepQualityScores.length > 0 ? sleepQualityScores[sleepQualityScores.length - 1] : 0,
      average_duration: weightedAverage(sleepDurations),
      latest_duration: sleepDurations.length > 0 ? sleepDurations[sleepDurations.length - 1] : 0,
      data_points: validSleepData.length
    };

    // Activity Stats (last 7 days, recency-weighted)
    const validActivityData = (athleteData.activity.activity_data || [])
      .filter(((data: any) => data && data.strain_score))
      .filter(a => isWithinLastNDays(a.date, 7))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    const strainScores = validActivityData.map(a => a.strain_score || 0);

    const activityStats = {
      average_strain: weightedAverage(strainScores),
      latest_strain: strainScores.length > 0 ? strainScores[strainScores.length - 1] : 0,
      total_strain: strainScores.reduce((a, b) => a + b, 0),
      data_points: validActivityData.length
    };

    return {
      recovery: recoveryStats,
      sleep: sleepStats,
      activity: activityStats
    };
  };

  const commands = useMemo(() => generateCommands({
    navigate,
    openCreateUserModal,
    openInviteUserModal,
    openCreateTeamModal,
    openCreateOrgModal,
    athletePageContext: { currentUrl: `/athlete/${teamId}/${athleteId}` }
  }), [navigate, openCreateUserModal, openInviteUserModal, openCreateTeamModal, openCreateOrgModal, teamId, athleteId]);

  /**
   * Get user info from localStorage
   */
  useEffect(() => {
    const userInfoString = localStorage.getItem('user_info');
    if (userInfoString) {
      try {
        const user = JSON.parse(userInfoString);
        setUserInfo(user);
      } catch (err) {
        console.error('Error parsing user info:', err);
      }
    }
  }, []);

  /**
   * Fetch comprehensive athlete data with AI analysis
   */
  useEffect(() => {
    const fetchAthleteData = async () => {
      if (!teamId || !athleteId) {
        setError('Missing team or athlete ID');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('access');
      if (!token) {
        navigate('/login');
        return;
      }

      try {
        setLoading(true);
        setAiLoading(true);

        const sourceParam = selectedDataSources.join(',');
        const response = await fetch(getApiUrl(`/api/teams/teams/${teamId}/athlete-detail/${athleteId}/?days=7&source=${sourceParam}`), {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          if (response.status === 401) {
            localStorage.removeItem('access');
            localStorage.removeItem('refresh');
            navigate('/login');
            return;
          }
          throw new Error(`Error fetching athlete data: ${response.status}`);
        }

        const data: AthleteDetailData = await response.json();
        setAthleteData(data);
        setAiLoading(false);
      } catch (err) {
        console.error('Error fetching athlete data:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch athlete data');
        setAiLoading(false);
      } finally {
        setLoading(false);
      }
    };

    fetchAthleteData();
  }, [teamId, athleteId, navigate, selectedDataSources]);

  /**
   * Generate recovery chart data - with data validation
   */
  const generateRecoveryChart = () => {
    if (!athleteData?.recovery.recovery_data) return { labels: [], datasets: [] };

    const now = new Date();
    const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const validData = athleteData.recovery.recovery_data
      .filter(((data: any) => data && data.recovery_score))
      .filter(d => {
        const t = new Date(d.date);
        return t >= start && t <= now;
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(-7);
    // Recovery data timestamp is the morning when the score was calculated (wake-up day)
    // No shift needed - the date already represents the correct day
    // Use noon local time to avoid timezone issues (UTC midnight shows as previous day in western timezones)
    const labels = validData.map(d => {
      return new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });

    return {
      labels,
      datasets: [
        {
          label: 'Recovery Score',
          data: validData.map(d => d.recovery_score),
          borderColor: '#67AEE6',
          backgroundColor: 'rgba(103, 174, 230, 0.1)',
          borderWidth: 3,
          pointBackgroundColor: '#67AEE6',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 2,
          pointRadius: 5,
          pointHoverRadius: 7,
          fill: true,
          tension: 0.4,
        },
        {
          label: 'HRV (RMSSD)',
          data: validData.map(d => ((data: any) => data && data.hrv_rmssd_milli)(d) ? d.hrv_rmssd_milli : null),
          borderColor: '#3B82F6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 2,
          pointBackgroundColor: '#3B82F6',
          pointBorderColor: '#ffffff',
          pointBorderWidth: 1,
          pointRadius: 3,
          fill: false,
          tension: 0.4,
          yAxisID: 'y1',
        }
      ],
    };
  };

  /**
   * Generate sleep chart data - with data validation
   */
  const generateSleepChart = () => {
    if (!athleteData?.sleep.sleep_data) return { labels: [], datasets: [] };

    const now = new Date();
    const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const validData = athleteData.sleep.sleep_data
      .filter((data: any) => data && data.total_duration_hours)
      .filter(d => {
        const t = new Date(d.date);
        return t >= start && t <= now;
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(-7);
    // Shift the dates forward by one day for display
    // Use noon local time to avoid timezone issues (UTC midnight shows as previous day in western timezones)
    const labels = validData.map(d => {
      const shiftedDateStr = shiftDateForward(d.date);
      return new Date(shiftedDateStr + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });

    return {
      labels,
      datasets: [
        {
          label: 'Sleep Quality',
          data: validData.map(d => d.sleep_quality_score || null),
          borderColor: '#7BA1BB',
          backgroundColor: 'rgba(123, 161, 187, 0.1)',
          borderWidth: 3,
          pointBackgroundColor: '#7BA1BB',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Duration (hours)',
          data: validData.map(d => d.total_duration_hours),
          borderColor: '#06B6D4',
          backgroundColor: 'rgba(6, 182, 212, 0.1)',
          borderWidth: 2,
          pointBackgroundColor: '#06B6D4',
          fill: false,
          tension: 0.4,
          yAxisID: 'y1',
        }
      ],
    };
  };

  /**
   * Generate activity/strain chart data - with data validation
   */
  const generateActivityChart = () => {
    if (!athleteData?.activity.activity_data) return { labels: [], datasets: [] };

    const now = new Date();
    const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const validData = athleteData.activity.activity_data
      .filter(((data: any) => data && data.strain_score))
      .filter(d => {
        const t = new Date(d.date);
        return t >= start && t <= now;
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(-7);
    // Activity/strain data timestamp is when the workout started
    // No shift needed - workouts occur during the day, not overnight
    // Use noon local time to avoid timezone issues (UTC midnight shows as previous day in western timezones)
    const labels = validData.map(d => {
      return new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });

    return {
      labels,
      datasets: [
        {
          label: 'Daily Strain',
          data: validData.map(d => d.strain_score || 0),
          borderColor: '#0093E7',
          backgroundColor: 'rgba(0, 147, 231, 0.1)',
          borderWidth: 3,
          pointBackgroundColor: '#0093E7',
          fill: true,
          tension: 0.4,
        }
      ],
    };
  };

  /**
   * Chart options for different metrics
   */
  const getChartOptions = (title: string, hasSecondaryAxis = false) => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: title,
        color: isDark ? '#F9FAFB' : '#111827',
        font: { size: 16, weight: 'bold' as const },
      },
      legend: {
        display: true,
        labels: {
          color: isDark ? '#D1D5DB' : '#374151',
          usePointStyle: true,
        },
      },
      tooltip: {
        backgroundColor: isDark ? '#0a0a0a' : '#FFFFFF',
        titleColor: isDark ? '#F9FAFB' : '#111827',
        bodyColor: isDark ? '#D1D5DB' : '#374151',
        borderColor: isDark ? '#374151' : '#E5E7EB',
        borderWidth: 1,
        titleFont: { weight: 'bold' as const, size: 14 },
        bodyFont: { size: 13 },
        padding: 12,
        callbacks: {
          label: (context: any) => {
            const label = context.dataset.label || '';
            const value = context.parsed.y != null ? context.parsed.y.toFixed(1) : 'N/A';
            return `${label}: ${value}`;
          },
          labelTextColor: (context: any) => {
            return context.dataset.borderColor || (isDark ? '#D1D5DB' : '#374151');
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: isDark ? '#9CA3AF' : '#6B7280' },
      },
      y: {
        grid: { color: isDark ? '#374151' : '#F3F4F6' },
        ticks: { color: isDark ? '#9CA3AF' : '#6B7280' },
      },
      ...(hasSecondaryAxis && {
        y1: {
          type: 'linear' as const,
          display: true,
          position: 'right' as const,
          grid: { drawOnChartArea: false },
          ticks: { color: isDark ? '#9CA3AF' : '#6B7280' },
        },
      }),
    },
  });

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen cgm-bg-primary">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center space-y-6">
            <div className="relative flex items-center justify-center">
              <div className="w-16 h-16 border-4 border-[var(--accent-ui-subtle)] border-t-[var(--spinner-color)] rounded-full animate-spin"></div>
            </div>
            <div>
              <h3 className="text-2xl font-bold theme-text-primary">Loading Athlete Analysis</h3>
              <p className="theme-text-secondary">Gathering comprehensive performance data...</p>
              {aiLoading && (
                <p className="cgm-text-secondary font-medium mt-2 flex items-center gap-2 justify-center">
                  <Bot className="w-5 h-5" /> AI analysis in progress...
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen cgm-bg-primary flex items-center justify-center p-6">
        <div className="max-w-md w-full">
          <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-2xl shadow-2xl p-8 text-center`}>
            <div className={`w-16 h-16 ${isDark ? 'bg-red-900/30' : 'bg-red-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
              <svg className={`w-8 h-8 ${isDark ? 'text-red-400' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h2 className={`text-xl font-bold ${isDark ? 'text-red-400' : 'text-red-600'} mb-4`}>Error Loading Data</h2>
            <p className="theme-text-secondary mb-6">{error}</p>
            <div className="space-y-3">
              <button
                onClick={() => window.location.reload()}
                className="cgm-button px-6 py-2 rounded-xl font-medium transition-all duration-200 w-full"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/coach-dashboard')}
                className={`${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'} theme-text-primary px-6 py-2 rounded-xl font-medium transition-all duration-200 w-full`}
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!athleteData) return null;

  return (
      <div className="min-h-screen cgm-bg-primary">
      {/* Navigation */}
      <Header
        userInfo={userInfo}
        isDark={isDark}
        commands={commands}
      />

      {/* Main Content */}
      <DashboardLayout>
        {/* Athlete Header */}
        <div className={`cgm-card rounded-2xl p-6 border ${isDark ? 'border-gray-700/50' : 'border-gray-200/50'} shadow-xl mb-8`}>
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="flex items-center space-x-4">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center font-bold text-xl ${isDark ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-900'}`}>
                {athleteData.athlete_info.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
              </div>
              <div>
                <h2 className="text-3xl font-bold theme-text-primary">{athleteData.athlete_info.name}</h2>
                <div className="flex items-center space-x-4 mt-1">
                  <div className="flex items-center space-x-2">
                    <div className={`w-3 h-3 rounded-full ${isAnyWearableConnected(athleteData.athlete_info) ? 'bg-green-500' : 'bg-red-500'}`}></div>
                    <span className="text-sm theme-text-secondary font-medium">
                      {getConnectedProviderLabel(athleteData.athlete_info)}
                    </span>
                  </div>
                  <span className="text-sm theme-text-secondary">•</span>
                  <span className="text-sm theme-text-secondary capitalize">{athleteData.athlete_info.role}</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-end space-y-2">
              <div className={`cgm-card px-4 py-2 rounded-xl`}>
                <span className="text-sm font-medium theme-text-primary flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" /> {athleteData.data_summary.analysis_period_days} Day Analysis
                </span>
              </div>
              <span className="text-xs theme-text-secondary">
                Generated: {new Date(athleteData.data_summary.analysis_date).toLocaleDateString()}
              </span>
            </div>
          </div>
        </div>

        {/* Data Source Filter */}
        {/* <DataSourceFilter
          selectedSources={selectedDataSources}
          connectedSources={connectedSources}
          onSourceChange={setSelectedDataSources}
        /> */}

        {/* Tab Navigation */}
        <TabNavigation
          tabs={[
            { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
            { id: 'recovery', label: 'Recovery', icon: 'Heart' },
            { id: 'sleep', label: 'Sleep', icon: 'Moon' },
            { id: 'activity', label: 'Activity', icon: 'Flame' },
            { id: 'ai-insights', label: 'AI Insights', icon: 'Bot' }
          ]}
          defaultTab="overview"
          paramName="tab"
        />

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <OverviewTab
            athleteData={athleteData}
            isDark={isDark}
            getValidatedStats={getValidatedStats}
          />
        )}

        {activeTab === 'recovery' && (
          <RecoveryTab
            athleteData={athleteData}
            isDark={isDark}
            generateRecoveryChart={generateRecoveryChart}
            getChartOptions={getChartOptions}
            dataSources={selectedDataSources}
          />
        )}

        {activeTab === 'readiness' && (
          <ReadinessScore
            athleteId={athleteId ? parseInt(athleteId) : undefined}
            isDark={isDark}
          />
        )}

        {activeTab === 'sleep' && (
          <SleepTab
            athleteData={athleteData}
            isDark={isDark}
            generateSleepChart={generateSleepChart}
            getChartOptions={getChartOptions}
            dataSources={selectedDataSources}
          />
        )}

        {activeTab === 'activity' && (
          <ActivityTab
            athleteData={athleteData}
            isDark={isDark}
            generateActivityChart={generateActivityChart}
            getChartOptions={getChartOptions}
            dataSources={selectedDataSources}
          />
        )}

        {activeTab === 'ai-insights' && (
          <AIInsightsTab
            athleteData={athleteData}
            isDark={isDark}
          />
        )}
      </DashboardLayout>
    </div>
  );
};

export default AthleteDetail;
